/**
 * The AffDice SuperAdmin.
 *
 * Every screen answers a question about work the platform has or has not done,
 * and all three of them fail silently by nature. An unanswered market throws
 * nothing. An altered audit record throws nothing. A stalled consumer throws
 * nothing — the events simply stop being processed. So this console exists to
 * make three silences visible, and that is most of what it is for.
 */

import { useEffect, useState, type ReactNode } from 'react';

import {
  Callout,
  ChangePassword,
  setCurrencyScales,
  NavList,
  PortalMark,
  Rail,
  RailIdentity,
  SignOut,
  ThemeToggle,
  TourButton,
  TourDialog,
  may,
  Loading,
  useLoad,
  useTour,
  type IconName,
  type OnboardApi,
} from '@affdice/portal-kit';

import { PlatformApi } from './client.ts';
import { t } from './messages.ts';
import { hashFor, routeFromHash, type RouteId } from './routes.ts';
import { AMS_TOUR_VERSION, amsTourSteps } from './tour.ts';
import { Dashboard } from './screens/overview/Overview.tsx';
import { Audit } from './screens/integrity/AuditChain.tsx';
import { Consumers } from './screens/integrity/Consumers.tsx';
import { Operators } from './screens/operators/Operators.tsx';
import { AllAffiliates } from './screens/affiliates/Affiliates.tsx';
import { Account } from './screens/platform/Account.tsx';
import { FeatureFlags } from './screens/platform/FeatureFlags.tsx';
import { ReferenceData } from './screens/platform/ReferenceData.tsx';
import { Users } from './screens/platform/Users.tsx';
import { RolesPermissions } from './screens/platform/RolesPermissions.tsx';
import { Plans } from './screens/platform/Plans.tsx';
import { Brands } from './screens/brands/Brands.tsx';
import { AccessLog } from './screens/integrity/AccessLog.tsx';
import { LoginAsHistory } from './screens/login-as/LoginAs.tsx';
import { AbilitiesProvider } from './screens/abilities.tsx';

/*
 * Six headings, by the thing being looked after: the platform's own health
 * first, then the three kinds of customer it holds (operators, their brands,
 * their affiliates), then how the platform itself is set up, then whether it
 * is intact. Platform and Integrity hold several screens each and show them
 * as a labelled run under the heading.
 */
function nav(): { id: RouteId; label: string; icon: IconName; group?: string }[] {
  return [
    // §8.6 (Architecture) — first: cross-tenant health and billing before any
    // one operational screen.
    { id: 'overview', label: t('nav.overview'), icon: 'dashboard' },
    { id: 'operators', label: t('nav.operators'), icon: 'operators' },
    { id: 'brands', label: t('nav.brands'), icon: 'brands' },
    // §3.9 — names and states across operators, so support can answer a basic
    // question about a customer. Not a way into anybody's commercial terms.
    { id: 'affiliates', label: t('nav.affiliates'), icon: 'affiliates' },
    // §3.9 — the platform's own people. An operator's team is theirs to manage.
    { id: 'users', label: t('nav.users'), icon: 'team', group: t('nav.group.platform') },
    { id: 'roles', label: t('nav.roles'), icon: 'settings', group: t('nav.group.platform') },
    // Item 7 — products, jurisdictions, currencies, devices, OS: DB-managed,
    // no longer hardcoded in three different files each.
    { id: 'reference-data', label: t('nav.referenceData'), icon: 'reference-data', group: t('nav.group.platform') },
    // Item 8 — TOTP and, going forward, any other flag: one global default
    // plus per-operator exemptions, managed here instead of an env var.
    { id: 'feature-flags', label: t('nav.featureFlags'), icon: 'feature-flags', group: t('nav.group.platform') },
    { id: 'plans', label: t('nav.plans'), icon: 'terms', group: t('nav.group.platform') },
    { id: 'audit', label: t('nav.audit'), icon: 'audit', group: t('nav.group.integrity') },
    { id: 'consumers', label: t('nav.consumers'), icon: 'consumers', group: t('nav.group.integrity') },
    { id: 'access-log', label: t('nav.accessLog'), icon: 'history', group: t('nav.group.integrity') },
    { id: 'login-as', label: t('nav.loginAs'), icon: 'audit', group: t('nav.group.integrity') },
  ];
}

/** The screen the address bar names, kept in step with Back, Forward and pasted links. */
function useRoute(): [RouteId, (r: RouteId) => void] {
  const [route, setRoute] = useState<RouteId>(() => routeFromHash(location.hash));

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash(location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return [route, (r: RouteId) => { location.hash = hashFor(r); setRoute(r); }];
}


export function App({
  api,
  permissions,
  userId,
  onboard,
  onSignedOut,
}: {
  api: PlatformApi;
  permissions: readonly string[];
  /** The signed-in user's own id — so a screen like Staff can tell "this row is me" apart from everyone else. */
  userId?: string;
  /** Item 2 (third batch) — Account's own voluntary "Change password". */
  onboard: OnboardApi;
  /**
   * What signing out returns to.
   *
   * Was `location.reload()`, which happens to work here because the session
   * lives in memory — and does nothing visible anywhere it does not.
   */
  onSignedOut?: () => void;
}): ReactNode {
  const [route, setRoute] = useRoute();
  // Item 2 (third batch) — every hook before the permission-gate's early
  // return below, since that return happens on some renders and not others.
  const context = useLoad(() => api.me(), []);
  /*
   * §1.3d — how many minor-unit digits each currency is written with.
   *
   * The same fetch the two consoles make at boot, for the same reason:
   * `ref_currencies.minor_unit_scale` never left the server, so every figure
   * here was written with two decimal places whatever its currency — and this
   * is the console that *edits* that column, which made it the one place the
   * discrepancy was invisible from both sides. Written during render and
   * idempotently, beside the locale the other consoles set here.
   */
  const currencyScales = useLoad(() => api.currencies(), []);
  if (currencyScales.state === 'ready') {
    setCurrencyScales(currencyScales.value.items);
  }
  const [accountOpen, setAccountOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  // The intro tour: starts by itself on a first sign-in, then only from the rail.
  const [tourStore] = useState(() => ({
    load: async () => {
      const r = await api.tour();
      return { seenVersion: r.seen_version, onBehalf: r.on_behalf };
    },
    save: (version: number) => api.markTourSeen(version),
  }));
  const tour = useTour({
    version: AMS_TOUR_VERSION,
    store: tourStore,
    steps: amsTourSteps((r) => { setAccountOpen(false); setRoute(r); }),
  });

  // `useRoute` already re-reads the screen on Back/Forward and pasted
  // links; Account sits outside that hash (it isn't a screen), so without
  // this it stayed open with no nav item current after the hash moved on.
  useEffect(() => {
    const onHash = () => setAccountOpen(false);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // §3.9 — the AffDice SuperAdmin is staff-only, and the API refuses anybody
  // else with a 404 besides. This is the courtesy, not the control.
  //
  // Item 4 (third batch) — `compliance.read` admits PLATFORM_USER
  // (migration 0091), the view-only tier that was locked out of the whole
  // console rather than shown it read-only as intended.
  if (
    !may(permissions, 'platform.admin') &&
    !may(permissions, 'platform.support') &&
    !may(permissions, 'compliance.read')
  ) {
    return (
      <div className="main">
        <Callout tone="caution">{t('shell.staffOnly')}</Callout>
      </div>
    );
  }

  // Item 4 — who may act, as opposed to who may look. The server enforces
  // this independently (server.ts refuses every non-GET from a platform
  // actor without one of these); this is what stops a button appearing that
  // would only fail when pressed.
  const canWrite = may(permissions, 'platform.admin') || may(permissions, 'platform.support');
  // Only the super admin role carries platform.admin; it alone may purge at
  // once instead of waiting out the retention window. Operators, Brands and
  // Affiliates read what the viewer may do capability by capability
  // (AbilitiesProvider); `canWrite` stays for the screens not yet moved.
  const isSuperAdmin = may(permissions, 'platform.admin');

  if (changingPassword && context.state === 'ready') {
    return (
      <ChangePassword
        email={context.value.me.email}
        api={onboard}
        title={t('shell.changePasswordTitle')}
        intro={t('shell.changePasswordIntro')}
        onDone={() => setChangingPassword(false)}
      />
    );
  }

  const meName = context.state === 'ready' ? (context.value.me.name || context.value.me.email) : '…';
  const meDetail = context.state === 'ready' && context.value.me.name ? context.value.me.email : undefined;

  return (
    <div className="shell">
      <Rail
        head={<PortalMark portal="ams" product="KD Affiliate SuperAdmin" context={t('shell.context')} />}
        foot={<>
          <TourButton tour={tour} />
          <RailIdentity
            name={meName}
            {...(meDetail ? { detail: meDetail } : {})}
            label={t('shell.account')}
            current={accountOpen}
            onOpen={() => setAccountOpen(true)}
          />
          <div className="rail-foot-row">
            {/* §3.7 — revoked at the server, not merely forgotten here. */}
            <SignOut
              revoke={() => api.logout()}
              onDone={onSignedOut ?? (() => location.reload())}
            />
            <ThemeToggle />
          </div>
        </>}
      >
        <NavList
          items={nav()}
          current={accountOpen ? null : route}
          onSelect={(id) => { setAccountOpen(false); setRoute(id); }}
        />
      </Rail>
      <TourDialog tour={tour} />

      <main className="main">
        <AbilitiesProvider api={api} superAdmin={isSuperAdmin}>
        {accountOpen ? (
          context.state === 'ready' ? (
            <Account me={context.value.me} onChangePassword={() => setChangingPassword(true)} />
          ) : (
            <Loading />
          )
        ) : (
          <>
            {/* Item 4 (third batch) — `canWrite` is false for PLATFORM_USER,
                who reaches every screen below and can act on none of them. */}
            {!canWrite ? (
              <Callout>{t('shell.readOnly')}</Callout>
            ) : null}
            {route === 'overview' ? <Dashboard api={api} onNavigate={setRoute} /> : null}
            {route === 'operators' ? <Operators api={api} /> : null}
            {route === 'brands' ? <Brands api={api} /> : null}
            {route === 'affiliates' ? <AllAffiliates api={api} {...(userId ? { currentUserId: userId } : {})} /> : null}
            {route === 'users' ? <Users api={api} canWrite={canWrite} userId={userId} /> : null}
            {/* The mount point for platform permissions: the screen fills in, the route stays. */}
            {route === 'roles' ? <RolesPermissions api={api} canWrite={canWrite} userId={userId} /> : null}
            {route === 'reference-data' ? <ReferenceData api={api} canWrite={canWrite} /> : null}
            {route === 'feature-flags' ? <FeatureFlags api={api} canWrite={canWrite} /> : null}
            {route === 'plans' ? <Plans /> : null}
            {route === 'audit' ? <Audit api={api} /> : null}
            {route === 'consumers' ? <Consumers api={api} /> : null}
            {route === 'access-log' ? <AccessLog api={api} /> : null}
            {route === 'login-as' ? <LoginAsHistory api={api} /> : null}
          </>
        )}
        </AbilitiesProvider>
      </main>
    </div>
  );
}

